import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { rpc } from '@/lib/rpc';
import { supabase } from '@/lib/supabase';

export const campaignKeys = {
  all: ['campaigns'] as const,
  list: () => ['campaigns', 'list'] as const,
  performance: () => ['campaigns', 'performance'] as const,
  quality: () => ['campaigns', 'quality'] as const,
  spend: (campaignId: string) => ['campaigns', 'spend', campaignId] as const,
};

export interface Campaign {
  id: string;
  platform: string;
  name: string;
  objective: string | null;
  external_ref: string | null;
  start_date: string | null;
  end_date: string | null;
  daily_budget: string | null;
  total_spend: string;
  target_areas: string[];
}

export interface CampaignPerformance {
  campaign_id: string;
  campaign_name: string;
  platform: string;
  spend: string;
  leads: number;
  qualified: number;
  visits: number;
  reservations: number;
  sales: number;
  revenue: string;
  commission: string;
  never_contacted: number;
  avg_response_seconds: number | null;
  cpl: string | null;
  cpql: string | null;
  cost_per_sale: string | null;
  qualified_rate: string | null;
  close_rate: string | null;
  roas: string | null;
}

export interface QualityRanking {
  campaign_id: string;
  campaign_name: string;
  platform: string;
  leads: number;
  qualified: number;
  cpl: string | null;
  cpql: string | null;
  roas: string | null;
  volume_rank: number;
  quality_rank: number;
  rank_gap: number;
}

export interface SpendRow {
  id: string;
  spend_date: string;
  amount: string;
  source: string;
  note: string | null;
}

// prettier-ignore
const CAMPAIGN_COLUMNS = 'id, platform, name, objective, external_ref, start_date, end_date, daily_budget, total_spend, target_areas';

// The two reporting views are not in the generated schema types, so PostgREST
// returns `any` for them. Narrowing through `unknown` keeps the lint rules honest:
// a direct assertion from `any` is flagged as unnecessary, and returning it raw is
// flagged as unsafe. This is the one place the shape is asserted, so the row
// interfaces above stay the single description of it.
const rows = <T>(data: unknown): T[] => (data ?? []) as T[];

export function useCampaigns() {
  return useQuery({
    queryKey: campaignKeys.list(),
    queryFn: async (): Promise<Campaign[]> => {
      const { data, error } = await supabase
        .from('campaigns')
        .select(CAMPAIGN_COLUMNS)
        .order('start_date', { ascending: false, nullsFirst: false });
      if (error) throw error;
      return rows<Campaign>(data);
    },
  });
}

/**
 * Reads the role-checked view, not the materialised view.
 *
 * Matviews cannot carry RLS, so mv_campaign_performance is revoked from every
 * client role and v_campaign_performance checks the caller's role instead.
 * Querying the matview directly from here would simply be denied — which is the
 * intended outcome, but worth knowing before debugging it.
 */
export function useCampaignPerformance() {
  return useQuery({
    queryKey: campaignKeys.performance(),
    queryFn: async (): Promise<CampaignPerformance[]> => {
      const { data, error } = await supabase
        .from('v_campaign_performance')
        .select('*')
        .order('spend', { ascending: false });
      if (error) throw error;
      return rows<CampaignPerformance>(data);
    },
  });
}

export function useCampaignQuality() {
  return useQuery({
    queryKey: campaignKeys.quality(),
    queryFn: async (): Promise<QualityRanking[]> => {
      const { data, error } = await supabase
        .from('v_campaign_quality_ranking')
        .select('*')
        .order('volume_rank');
      if (error) throw error;
      return rows<QualityRanking>(data);
    },
  });
}

export function useCampaignSpend(campaignId: string | undefined) {
  return useQuery({
    queryKey: campaignId ? campaignKeys.spend(campaignId) : ['campaigns', 'spend', 'none'],
    enabled: Boolean(campaignId),
    queryFn: async (): Promise<SpendRow[]> => {
      const { data, error } = await supabase
        .from('campaign_spend')
        .select('id, spend_date, amount, source, note')
        .eq('campaign_id', campaignId!)
        .order('spend_date', { ascending: false })
        .limit(90);
      if (error) throw error;
      return rows<SpendRow>(data);
    },
  });
}

function useCampaignMutation<TVars>(fn: (vars: TVars) => Promise<unknown>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: campaignKeys.all }),
  });
}

export function useUpsertCampaign() {
  return useCampaignMutation(
    (vars: {
      name: string;
      id?: string | null;
      platform?: string;
      objective?: string | null;
      externalRef?: string | null;
      startDate?: string | null;
      endDate?: string | null;
      dailyBudget?: number | null;
      targetAreas?: string[];
    }) =>
      rpc<Campaign>('upsert_campaign', {
        p_name: vars.name,
        p_id: vars.id ?? null,
        p_platform: vars.platform ?? 'meta',
        p_objective: vars.objective ?? null,
        p_external_ref: vars.externalRef ?? null,
        p_start_date: vars.startDate ?? null,
        p_end_date: vars.endDate ?? null,
        p_daily_budget: vars.dailyBudget ?? null,
        p_target_areas: vars.targetAreas ?? [],
      }),
  );
}

export function useRecordSpend() {
  return useCampaignMutation(
    (vars: { campaignId: string; spendDate: string; amount: number; note?: string | undefined }) =>
      rpc<SpendRow>('record_campaign_spend', {
        p_campaign_id: vars.campaignId,
        p_spend_date: vars.spendDate,
        p_amount: vars.amount,
        p_note: vars.note ?? null,
      }),
  );
}
